import { Component, OnDestroy, OnInit, Optional } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ACTION_LOADING } from 'src/app/component/store/actions/actions';
import { FEATURES_EXCLUDE_SIDEBAR } from 'src/app/interface/features.interface';
import { MenuRegenerationService } from 'src/app/services/menu-regeneration.service';
import { ReservationsStackFeatureService } from 'src/app/services/reservations-stack-feature.service';
import { WhiteLabelService } from 'src/app/services/white-label.service';
import { DataService } from '../../services/data.service';
import { GeneralConfigService } from '../../services/general-config.service';
import { ROUTES, SUB_MENU_ITEMS, mainFeatures } from './menu-items';
import { WHITE_LABEL_DROPDOWN_SUB_ITEMS, WHITE_LABEL_FEATURE_MAPPING } from './menu-availability';
import { RecordLog, RoundControl, ShiftControl } from 'src/app/interface/buildings.interface';
import { PROPRIETOR_ALLOWED_MAIN_FEATURES } from '../constants/proprietor-access.constants';
import {
  shouldShowAccountStatementsMenu,
  shouldShowMunilyPayMenuItem,
} from '../constants/financial-menu-items.constants';
import { SIDEBAR_DEFAULT_LOGO_FULL_URL, SIDEBAR_DEFAULT_LOGO_RAIL_URL } from './constants/sidebar-logo.constants';
import { SidebarManualExpandState } from './constants/sidebar-manual-expand-state.enum';
import { UserRoles } from 'src/app/interface/users.interface';

enum MacroBuildingType {
  macrocommunity = 'macrocommunity',
  community = 'community',
}

export enum WhiteLabelTypes {
  munily = 'munily',
  autodromo = 'autodromo',
  MTS = 'MTS',
}
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  showMenu = '';
  showSubMenu = '';
  currentLanguage: string = 'es';
  currentUrl: string = '';
  isRailMode: boolean = false;
  public sidebarnavItems = [];
  contability: boolean = false;
  private menuRegenerationSubscription: Subscription;
  private stackReadySubscription?: Subscription;
  private generalConfigSubscription?: Subscription;
  boardsRecordLog: RecordLog = {
    enable: false,
    link: '',
  };
  boardsRoundControl: RoundControl = {
    enable: false,
  };
  boardsShiftControl: ShiftControl = {
    enable: false
  };
  logoRailMode: string = SIDEBAR_DEFAULT_LOGO_RAIL_URL;
  logoFullMode: string = SIDEBAR_DEFAULT_LOGO_FULL_URL;
  private readonly HOME_MENU_PATHS = new Set([
    ROUTES[mainFeatures.dashboard].path,
    ROUTES[mainFeatures.dashboardMacro].path,
    ROUTES[mainFeatures.dashboardSuperAdmin].path,
  ]);

  constructor(
    public dataService: DataService,
    public generalConfigService: GeneralConfigService,
    private translateService: TranslateService,
    private router: Router,
    private menuRegenerationService: MenuRegenerationService,
    private whiteLabelService: WhiteLabelService,
    @Optional() private reservationsStackFeature?: ReservationsStackFeatureService,
  ) { }

  async ngOnInit() {
    this.currentLanguage = this.translateService.store.currentLang;
    this.setupRouterEvents();
    this.setupMenuRegeneration();
    this.setLogoUrl();

    if (this.reservationsStackFeature) {
      this.reservationsStackFeature.whenReady?.().subscribe();
      this.stackReadySubscription = this.reservationsStackFeature.ready$
        .pipe(filter((ready) => ready === true))
        .subscribe(() => {
          this.buildMenu();
        });
    } else {
      setTimeout(() => {
        this.buildMenu();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    if (this.menuRegenerationSubscription) {
      this.menuRegenerationSubscription.unsubscribe();
    }
    this.stackReadySubscription?.unsubscribe();
    this.generalConfigSubscription?.unsubscribe();
  }

  // Configura la suscripción para regenerar el menú cuando cambie de sesión
  private setupMenuRegeneration(): void {
    this.menuRegenerationSubscription = this.menuRegenerationService.menuRegeneration$.subscribe(() => {
      this.regenerateMenu();
    });
  }

  public regenerateMenu(): void {
    this.setLogoUrl();
    if (this.reservationsStackFeature?.isReady === false) {
      return;
    }
    this.sidebarnavItems = [];
    this.buildMenu();
  }

  private buildMenu(): void {
    this.generalConfigSubscription?.unsubscribe();
    this.sidebarnavItems = [];
    const user = this.dataService.getUser();
    const building = this.dataService.getBuilding();
    const buildingCofig = building.config;

    const roles = user.roles ?? [''];
    const isMacro: boolean = !!user.buildings[0].config?.macrocommunity?.enable;
    const isAMacroChild: boolean =
      isMacro && user.buildings[0].config?.macrocommunity?.communities ? !this.isAMacroFather(user.buildings[0].config?.macrocommunity.communities) : false;
    const recordLog = buildingCofig?.recordLog;
    const roundControl = buildingCofig?.roundControl;
    const shiftControl = buildingCofig?.shiftControl;

    if (recordLog || roundControl || shiftControl) {
      if (shiftControl?.enable && shiftControl?.boardLink) {
        this.boardsShiftControl.enable = true;
        this.boardsShiftControl.boardLink = shiftControl?.boardLink;
      }

      if (recordLog?.enable && recordLog?.boardLink) {
        this.boardsRecordLog.enable = true;
        this.boardsRecordLog.boardLink = recordLog?.boardLink;
      }
      if (roundControl?.enable && roundControl?.boardLink) {
        this.boardsRoundControl.enable = true;
        this.boardsRoundControl.boardLink = roundControl?.boardLink;
      }
    }

    const buildingId = building?.id;
    // Obtener features del menú desde la config de white label
    const showFeatures = this.whiteLabelService.getMenuShowFeatures(building);
    const isCommitee = roles.indexOf(UserRoles.committee) > -1;

    if (user.superUser) {
      this.buildSuperAdminMenu();
    } else if (roles.indexOf('proprietor') > -1 && roles.indexOf('admin') === -1) {
      this.buildProprietorMenu();
    } else if (roles.indexOf('customized') > -1) {
      this.buildCustomizedMenu(user.features);
    } else if (showFeatures && showFeatures.length > 0) {
      // Usar config de white label si está disponible
      this.buildWhiteLabelMenu(showFeatures, roles, isMacro, isAMacroChild, buildingCofig);
    } else if (isCommitee) {
      this.buildCommitteeMenu();
    } else {
      // Sin config white label - usar menú estándar
      this.buildStandardMenu(roles, isMacro, isAMacroChild, buildingCofig);
    }

    // Get the current URL
    this.currentUrl = this.router.url;
    this.expandMenuIfRouteMatches();
  }

  /** Construye el menú basado en showFeature de la config white label. */
  private buildWhiteLabelMenu(showFeatures: string[], roles: any, isMacro: boolean, isAMacroChild: boolean, buildingCofig: any): void {
    showFeatures = this.withTicketsV2WhenPqrsShowFeature(showFeatures);
    // Mapeo de features del backend a mainFeatures del dashboard (VDS-3315: single source of
    // truth shared with NavigationService.resolvePostSwitchRoute — see menu-availability.ts).
    const featureMapping: Record<string, string> = WHITE_LABEL_FEATURE_MAPPING;

    // Definición de qué sub-items pertenecen a cada dropdown
    const dropdownSubItems: Record<string, string[]> = WHITE_LABEL_DROPDOWN_SUB_ITEMS;

    // Convertir features del backend a mainFeatures
    const mappedFeatures = showFeatures.map((feature) => featureMapping[feature] || feature).filter((item) => ROUTES[item]);

    // Separar en items principales y sub-items
    const mainItems: string[] = [];
    const subItemsToInclude: Record<string, string[]> = {};

    mappedFeatures.forEach((feature) => {
      // Verificar si es un sub-item de algún dropdown
      let isSubItem = false;
      for (const [dropdown, subItems] of Object.entries(dropdownSubItems)) {
        if (subItems.includes(feature)) {
          isSubItem = true;
          if (!subItemsToInclude[dropdown]) {
            subItemsToInclude[dropdown] = [];
          }
          subItemsToInclude[dropdown].push(feature);
          break;
        }
      }

      // Si no es sub-item, es un item principal
      if (!isSubItem) {
        // Si es un dropdown completo, agregar todos sus sub-items
        if (dropdownSubItems[feature]) {
          subItemsToInclude[feature] = [...dropdownSubItems[feature]];
        } else {
          mainItems.push(feature);
        }
      }
    });

    // Construir dropdowns con solo los sub-items especificados
    for (const [dropdown, subItems] of Object.entries(subItemsToInclude)) {
      if (ROUTES[dropdown] && subItems.length > 0) {
        const dropdownRoute = ROUTES[dropdown];
        const filteredSubmenu = subItems.map((subItem) => ROUTES[subItem]).filter(Boolean);

        if (filteredSubmenu.length > 0) {
          const customDropdown = {
            ...dropdownRoute,
            submenu: this.sortListByTitle([...filteredSubmenu]),
          };
          this.sidebarnavItems.push(customDropdown);
        }
      }
    }

    // Agregar items principales
    mainItems.forEach((item) => {
      if (ROUTES[item]) {
        this.sidebarnavItems.push(ROUTES[item]);
      }
    });

    // Ordenar el menú
    this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
    this.adjacentManagementVisitsBeforeVisits(this.sidebarnavItems);

    // Si es macro community, aplicar filtros adicionales
    if (isMacro && !isAMacroChild) {
      this.sidebarnavItems = this.sidebarnavItems.filter(
        (item) => item !== ROUTES[mainFeatures.knowledgeCenter] && item !== ROUTES[mainFeatures.dashboardMacro],
      );
    }

    this.setCurrentUrlAndExpand();
  }

  /** Tras ordenar por título, deja Gestión de visitas inmediatamente antes de Visitas si ambos existen como ítems de primer nivel (menú white label). */
  private adjacentManagementVisitsBeforeVisits(navItems: any[]): void {
    const visitsRoute = ROUTES[mainFeatures.visits];
    const managementCandidates = [
      ROUTES[mainFeatures.managementVisitsMacro],
      ROUTES[mainFeatures.managementVisitsMacroBusiness],
    ];

    let managementIdx = -1;
    let visitsIdx = -1;
    for (let i = 0; i < navItems.length; i++) {
      const item = navItems[i];
      if (managementIdx === -1 && managementCandidates.some((r) => r === item)) {
        managementIdx = i;
      }
      if (item === visitsRoute) {
        visitsIdx = i;
      }
    }

    if (managementIdx === -1 || visitsIdx === -1) {
      return;
    }

    const [managementItem] = navItems.splice(managementIdx, 1);
    const adjustedVisitsIdx = visitsIdx > managementIdx ? visitsIdx - 1 : visitsIdx;
    navItems.splice(adjustedVisitsIdx, 0, managementItem);
  }

  // Construye el menú para propietario
  private buildProprietorMenu(): void {
    const items = PROPRIETOR_ALLOWED_MAIN_FEATURES.map((key) => {
      if (key === mainFeatures.administration) {
        return this.getProprietorAdministrativeItem();
      }
      if (key === mainFeatures.managementVisitsMacroBusiness) {
        const managementKey = this.dataService.getBuildingIsMacroFather()
          ? mainFeatures.managementVisitsMacro
          : mainFeatures.managementVisitsMacroBusiness;
        return ROUTES[managementKey];
      }
      return ROUTES[key];
    });
    this.sidebarnavItems = [...this.sortListByTitle(items)];
    this.adjacentManagementVisitsBeforeVisits(this.sidebarnavItems);
  }

  private getProprietorAdministrativeItem(): any {
    return {
      ...ROUTES[mainFeatures.administrativeDropdown],
      title: ROUTES[mainFeatures.administrativeDropdown].title,
      submenu: [ROUTES[mainFeatures.users]],
    };
  }

  // Construye el menú para super admin
  private buildSuperAdminMenu(): void {
    this.sidebarnavItems.push(ROUTES[mainFeatures.buildings]);
    this.sidebarnavItems.push(ROUTES[mainFeatures.usersSuperAdmin]);
    this.sidebarnavItems.push(ROUTES[mainFeatures.services]);
    this.sidebarnavItems.push(ROUTES[mainFeatures.releases]);
    this.sidebarnavItems.push(ROUTES[mainFeatures.knowledgeCenter]);
    this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
    this.sidebarnavItems = [ROUTES[mainFeatures.dashboardSuperAdmin], ...this.sidebarnavItems];
    this.sidebarnavItems.push(ROUTES[mainFeatures.maia]);
    this.sidebarnavItems.push(ROUTES[mainFeatures.legalPolicies]);
  }

  // Construye el menú para usuarios customized
  private buildCustomizedMenu(features: any[]): void {
    this.builtCustomizedMenu(features);
    this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
  }

  private builtCustomizedMenu(features) {
    features.forEach((feature) => {
      if (feature.enable) {
        feature.feature.forEach((userFeature) => {
          if (FEATURES_EXCLUDE_SIDEBAR.indexOf(userFeature) < 0) {
            // Feature is a subMenu item
            if (this.checkIsSubmenuItem(userFeature)) {
              const mainMenu = this.getMenuItem(userFeature);
              if (!ROUTES[mainMenu].submenu.includes(ROUTES[userFeature])) ROUTES[mainMenu].submenu.push(ROUTES[userFeature]);
              //Validate if parent Menu Item is in the sideBarNav list, if not added
              if (this.sidebarnavItems.indexOf(ROUTES[mainMenu]) < 0) this.sidebarnavItems.push(ROUTES[mainMenu]);
            } else {
              //Feature is a main menu item
              if (this.sidebarnavItems.indexOf(ROUTES[userFeature]) < 0) this.sidebarnavItems.push(ROUTES[userFeature]);
            }
          }
        });
        if (this.sidebarnavItems.includes(ROUTES[mainFeatures.visits])) {
          const typeRouter = this.dataService.getBuildingIsMacroFather() ? mainFeatures.visitsMacro : mainFeatures.visitsMacroBusiness;
          const index = this.sidebarnavItems.indexOf(ROUTES[mainFeatures.visits]);
          this.sidebarnavItems[index] = ROUTES[typeRouter];
        }

        if (this.sidebarnavItems.includes(ROUTES[mainFeatures.managementVisitsMacroBusiness])) {
          const typeRouter = this.dataService.getBuildingIsMacroFather() ? mainFeatures.managementVisitsMacro : mainFeatures.managementVisitsMacroBusiness;
          const index = this.sidebarnavItems.indexOf(ROUTES[mainFeatures.managementVisitsMacro]);
          this.sidebarnavItems[index] = ROUTES[typeRouter];
        }
      }
    });
  }

  // Construye el menú para administradores estándar
  private buildStandardMenu(roles: any, isMacro: boolean, isAMacroChild: boolean, buildingCofig: any, excludedMainItems?: string[]): void {
    if (roles.indexOf('admin') > -1 && (!isMacro || isAMacroChild)) {
      //STANDAR ADMINISTRATOR
      this.buildTraditionalAdminMenu(buildingCofig, excludedMainItems);
    } else if (roles.indexOf('admin') > -1 && isMacro && !isAMacroChild) {
      //MACROCOMMUNITY ADMINISTRATORS
      this.buildMacroAdminMenu(excludedMainItems);
    }
  }

  // Construye el menú para administradores tradicionales
  private buildTraditionalAdminMenu(buildingCofig: any, excludedMainItems?: string[]): void {
    this.dataService.isLoading({ action: ACTION_LOADING, payload: true });
    this.generalConfigSubscription?.unsubscribe();
    this.generalConfigSubscription = this.generalConfigService.getGeneralConfigFeatures().subscribe(
      (config: any) => {
        this.sidebarnavItems = [];
        //CHAT
        if (buildingCofig.chatV2 && buildingCofig.chatV2.enable) {
          this.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.chats]);
        }
        if (config.data.feature && config.data.feature.contability) this.contability = true;
        if (shouldShowAccountStatementsMenu(this.contability, buildingCofig.accountState)) {
          this.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.accountStatements]);
        }
        // Tickets v2 — mismo alcance que PQRS (admin PH con módulo habilitado).
        if (buildingCofig.pqrs && buildingCofig.pqrs.enable) {
          this.sidebarnavItems.splice(1, 0, ROUTES[mainFeatures.ticketsV2]);
        }
        if (shouldShowMunilyPayMenuItem(this.dataService.getUser(), buildingCofig.fintech)) {
          this.sidebarnavItems.push(ROUTES[mainFeatures.munilyPayTransactions]);
        }
        //AccessControl
        const accessControl = buildingCofig.accessControl;
        if (accessControl && accessControl.preRegisterVisit && (accessControl.preRegisterVisit.enable || accessControl.vigilantVisit.enable)) {
          this.sidebarnavItems.push(ROUTES[mainFeatures.controlAccess]);
        }
        //Calls - Llamadas
        if (buildingCofig.directory?.enable || buildingCofig.invitation?.directCallVigilant) {
          this.sidebarnavItems.push(ROUTES[mainFeatures.vigilantCalls]);
        }
        //Munily Providers
        if (buildingCofig.munilyPh && buildingCofig.munilyPh.enable) {
          if (buildingCofig.munilyPh.link) ROUTES[mainFeatures.munilyProviders].path = buildingCofig.munilyPh.link;
          this.sidebarnavItems.push(ROUTES[mainFeatures.munilyProviders]);
        }

        if (this.boardsRecordLog.enable || this.boardsRoundControl.enable || this.boardsShiftControl.enable) {
          const boards = ROUTES[mainFeatures.boards];
          boards.submenu = [];


          if (this.boardsShiftControl.enable) {
            boards.submenu.push(ROUTES[mainFeatures.shiftControl]);
          }

          if (this.boardsRecordLog.enable) {
            boards.submenu.push(ROUTES[mainFeatures.recordLog]);
          }

          if (this.boardsRoundControl.enable) {
            boards.submenu.push(ROUTES[mainFeatures.roundControl]);
          }
          this.sidebarnavItems.push(ROUTES[mainFeatures.boards]);
        }

        //General Items
        this.sidebarnavItems.push(
          ROUTES[mainFeatures.notices],
          ROUTES[mainFeatures.reservations],
          ROUTES[mainFeatures.visits],
          ROUTES[mainFeatures.frequentAccess],
          ROUTES[mainFeatures.administrativeDropdown],
          ROUTES[mainFeatures.knowledgeCenter],
          ROUTES[mainFeatures.emergency],
        );
        this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
        this.filterMenuItemsByKeys(excludedMainItems);
        //Setting Home after sorting
        this.sidebarnavItems = [ROUTES[mainFeatures.dashboard], ...this.sidebarnavItems];
        this.dataService.isLoading({ action: ACTION_LOADING, payload: false });
      },
      (err) => {
        this.dataService.isLoading({ action: ACTION_LOADING, payload: false });
        console.log(err.error.message);
      },
    );
  }

  private withTicketsV2WhenPqrsShowFeature(showFeatures: string[]): string[] {
    if (!showFeatures.includes('pqrs') || showFeatures.includes('ticketsV2')) {
      return showFeatures;
    }
    return [...showFeatures, 'ticketsV2'];
  }

  // Construye el menú para administradores de macro-community
  private buildMacroAdminMenu(excludedMainItems?: string[]): void {
    this.sidebarnavItems.push(
      ROUTES[mainFeatures.noticesMacro],
      ROUTES[mainFeatures.reservationsMacro],
      ROUTES[mainFeatures.administrativeMacroDropdown],
      ROUTES[mainFeatures.knowledgeCenter],
    );
    this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
    this.filterMenuItemsByKeys(excludedMainItems);
    this.sidebarnavItems = [ROUTES[mainFeatures.dashboardMacro], ...this.sidebarnavItems];
  }

  private filterMenuItemsByKeys(excludedMainItems?: string[]): void {
    if (!excludedMainItems || excludedMainItems.length === 0) return;

    this.sidebarnavItems = this.sidebarnavItems.filter((menuItem) => {
      const menuItemKey = Object.keys(mainFeatures).find((key) => ROUTES[mainFeatures[key]] === menuItem);
      return !menuItemKey || !excludedMainItems.includes(menuItemKey);
    });
  }

  private setCurrentUrlAndExpand(): void {
    this.currentUrl = this.router.url;
    this.expandMenuIfRouteMatches();
  }

  private setupRouterEvents(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
        this.expandMenuIfRouteMatches();
      }
    });
  }

  private checkIsSubmenuItem(userFeature) {
    const subItem = SUB_MENU_ITEMS.find((element) => element.submenu === userFeature);
    if (subItem) return true;
    return false;
  }

  private getMenuItem(userFeature) {
    const subItem = SUB_MENU_ITEMS.find((element) => element.submenu === userFeature);
    if (subItem) return subItem.main;
  }

  toggleRailMode(sideNav) {
    this.isRailMode = !this.isRailMode;
    this.showMenu = SidebarManualExpandState.Collapsed;
  }

  isActive(itemPath: string): boolean {
    if (!itemPath) return false;
    // Coincidencia exacta o si la ruta actual comienza con la ruta del item (para rutas hijas)
    return this.currentUrl === itemPath || this.currentUrl.startsWith(itemPath + '/');
  }

  addExpandClass(element: any) {
    if (element === this.showMenu) {
      this.showMenu = SidebarManualExpandState.Collapsed;
    } else {
      this.showMenu = element;
      this.isRailMode = false;
    }
  }

  addActiveClass(element: any) {
    if (element === this.showSubMenu) {
      this.showSubMenu = SidebarManualExpandState.Collapsed;
    } else {
      this.showSubMenu = element;
    }
  }

  private isHomeMenuItem(item: { path?: string }): boolean {
    return !!item?.path && this.HOME_MENU_PATHS.has(item.path);
  }

  private ensureHomeFirst(list: any[]): any[] {
    const homeItems = list.filter((item) => this.isHomeMenuItem(item));
    if (!homeItems.length) return list;
    const otherItems = list.filter((item) => !this.isHomeMenuItem(item));
    return [...homeItems, ...otherItems];
  }

  private sortListByTitle(list) {
    const sorted = list.sort((a, b) => {
      if (a.submenu?.length > 0) {
        a.submenu = this.sortListByTitle(a.submenu);
      }
      const titleA = this.translateService.instant(a.title).toLowerCase();
      const titleB = this.translateService.instant(b.title).toLowerCase();

      return titleA.localeCompare(titleB, 'es', { sensitivity: 'base' });
    });
    return this.ensureHomeFirst(sorted);
  }

  isAMacroFather(communities: any[]): boolean {
    const buildingFatherType = MacroBuildingType.macrocommunity;
    const hasFatherType = communities.findIndex((community) => {
      return community.type == buildingFatherType;
    });
    return hasFatherType > -1;
  }

  handleMenuItemClick(item: any): void {
    if (item.externalLink) {
      // Handle external link
      window.open(item.path, '_blank');
    } else if (item.submenu?.length > 0) {
      // Handle submenu expansion
      this.addExpandClass(item.title);
    } else {
      // Handle internal navigation
      this.addActiveClass(item.title);
      const target = this.reservationsStackFeature
        ? this.reservationsStackFeature.resolveMenuPath(item.path)
        : item.path;
      this.router.navigateByUrl(target);
    }
  }

  setLogoUrl(): void {
    const building = this.dataService.getBuilding();

    // Primero intentar obtener logos desde la nueva config whiteLabel.projects.dashboard
    const logoRailUrl = this.whiteLabelService.getMenuLogoRailUrl(building);
    const logoFullUrl = this.whiteLabelService.getMenuLogoFullUrl(building);

    if (logoRailUrl) {
      this.logoRailMode = logoRailUrl;
    }
    if (logoFullUrl) {
      this.logoFullMode = logoFullUrl;
    }

    // Si no hay config nueva, mantener compatibilidad con config legacy (whiteLabel.params.dashboard)
    if (!logoRailUrl && !logoFullUrl) {
      const whiteLabelConfig = building?.config?.whiteLabel;
      if (whiteLabelConfig && whiteLabelConfig.type && whiteLabelConfig.type === WhiteLabelTypes.MTS) {
        const params = whiteLabelConfig.params;
        if (params && params.dashboard) {
          const dashboardConfig = params.dashboard;
          const companyConfig = dashboardConfig.company;
          if (companyConfig && companyConfig.logoRail) {
            this.logoRailMode = companyConfig.logoRail;
          }
          if (companyConfig && companyConfig.logoFull) {
            this.logoFullMode = companyConfig.logoFull;
          }
        }
      }
    }
  }

  /**
   * Expande automáticamente el menú si la ruta actual coincide con algún submenu.
   * Especialmente útil para edificios con menú personalizado que tienen un solo elemento.
   */
  private expandMenuIfRouteMatches(): void {
    if (!this.currentUrl) return;

    // Buscar en todos los items del menú
    for (const menuItem of this.sidebarnavItems) {
      // Si el item tiene submenu, verificar si algún submenu coincide con la ruta actual
      if (menuItem.submenu && menuItem.submenu.length > 0) {
        const matchingSubmenu = menuItem.submenu.find((subItem: any) => {
          // Verificar coincidencia exacta o si la ruta actual comienza con la ruta del submenu
          return subItem.path && (this.currentUrl === subItem.path || this.currentUrl.startsWith(subItem.path + '/'));
        });

        if (matchingSubmenu) {
          // Expandir el menú padre
          this.showMenu = menuItem.title;
          break;
        }
      }
    }
  }

  private buildCommitteeMenu(): void {
    this.sidebarnavItems.push(ROUTES[mainFeatures.committeeDashboard]);
    this.sidebarnavItems = this.sortListByTitle(this.sidebarnavItems);
  }
}
